import React from "react";
import { Container, Typography, Box, Divider } from "@mui/material";
import { useLanguage } from "../../contexts/LanguageContext";

interface Section {
  heading: string;
  content: string | string[];
}

interface Chapter {
  title: string;
  sections: Section[];
}

const DatenschutzPage: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const isAr = currentLanguage === "ar";

  const chapters: Chapter[] =
    currentLanguage === "ar"
      ? [
          {
            title: "مقدمة",
            sections: [
              {
                heading: "تمهيد",
                content:
                  "نحن، TOP Buchhaltungsservice UG، Stuttgartstraße 15-17، 44143 Dortmund، نأخذ حماية بياناتك الشخصية بجدية تامة ونودّ إعلامك بممارسات الخصوصية المتبعة لدينا.",
              },
            ],
          },
          {
            title: "أ. أحكام عامة",
            sections: [
              {
                heading: "الجهة المسؤولة",
                content: [
                  "TOP Buchhaltungsservice UG",
                  "Stuttgartstraße 15-17",
                  "44143 Dortmund",
                  "الهاتف: +49 15755659651",
                  "البريد الإلكتروني: info@top-bhs.de",
                  "الموقع: https://www.top-bhs.de/impressum",
                ],
              },
              {
                heading: "مسؤول حماية البيانات",
                content: [
                  "TOP Buchhaltungsservice UG",
                  "Stuttgartstraße 15-17",
                  "44143 Dortmund",
                  "info@top-bhs.de",
                ],
              },
              {
                heading: "الأساس القانوني لمعالجة البيانات",
                content:
                  "تتم معالجة البيانات الشخصية وفق اللوائح الأوروبية (DSGVO) وتشمل الأسس القانونية: الموافقة (المادة 6/1/أ)، تنفيذ العقد (المادة 6/1/ب)، الالتزامات القانونية (المادة 6/1/ج)، والمصالح المشروعة (المادة 6/1/و).",
              },
              {
                heading: "الحذف ومدة التخزين",
                content:
                  "يتم حذف البيانات الشخصية أو تقييدها فور انتفاء الغرض من تخزينها. قد يمتد التخزين في حالات النزاعات القانونية أو الالتزامات القانونية (§ 257 HGB، § 147 AO).",
              },
              {
                heading: "أمان البيانات",
                content:
                  "نتخذ تدابير تقنية وتنظيمية مناسبة لحماية بياناتك من التلاعب أو الفقدان أو الوصول غير المصرح به، بما في ذلك تشفير TLS لموقعنا الإلكتروني.",
              },
              {
                heading: "حقوقك",
                content: [
                  "• الحق في الاطلاع على بياناتك (المادة 15)",
                  "• الحق في تصحيح البيانات غير الدقيقة (المادة 16)",
                  "• الحق في حذف البيانات (المادة 17)",
                  "• الحق في تقييد المعالجة (المادة 18)",
                  "• الحق في نقل البيانات (المادة 20)",
                  "• الحق في الاعتراض (المادة 21)",
                  "• الحق في تقديم شكوى لدى سلطة حماية البيانات",
                  "الجهة المختصة: LDI NRW، Kavalleriestr. 2-4، 40213 Düsseldorf",
                ],
              },
            ],
          },
          {
            title: "ب. زيارة الموقع الإلكتروني",
            sections: [
              {
                heading: "البيانات المعالجة",
                content: [
                  "بيانات السجل (Server-Logfiles): عنوان IP المختصر، المتصفح، نظام التشغيل، الصفحة المطلوبة، التاريخ والوقت.",
                  "بيانات نماذج التواصل: الاسم، البريد الإلكتروني، رقم الهاتف، الشركة، الرسالة.",
                ],
              },
              {
                heading: "الغرض والأساس القانوني",
                content:
                  "تُعالَج بيانات السجل لأغراض إحصائية وأمان الموقع (المادة 6/1/و). وتُعالَج بيانات نماذج التواصل لمعالجة الاستفسارات (المادة 6/1/ب أو 6/1/و).",
              },
              {
                heading: "ملفات تعريف الارتباط (Cookies)",
                content:
                  "نستخدم ملفات تعريف الارتباط الضرورية تقنياً فقط لضمان عمل الموقع بشكل صحيح. لا نستخدم ملفات تعريف الارتباط لأغراض التسويق أو التتبع.",
              },
            ],
          },
        ]
      : currentLanguage === "en"
        ? [
            {
              title: "Introduction",
              sections: [
                {
                  heading: "Preamble",
                  content:
                    "We, TOP Buchhaltungsservice UG, Stuttgartstraße 15-17, 44143 Dortmund, take the protection of your personal data seriously and would like to inform you about our data protection practices.",
                },
              ],
            },
            {
              title: "A. General",
              sections: [
                {
                  heading: "Controller",
                  content: [
                    "TOP Buchhaltungsservice UG",
                    "Stuttgartstraße 15-17",
                    "44143 Dortmund",
                    "Phone: +49 15755659651",
                    "Email: info@top-bhs.de",
                    "Website: https://www.top-bhs.de/impressum",
                  ],
                },
                {
                  heading: "Data Protection Officer",
                  content: [
                    "TOP Buchhaltungsservice UG",
                    "Stuttgartstraße 15-17",
                    "44143 Dortmund",
                    "info@top-bhs.de",
                  ],
                },
                {
                  heading: "Legal Basis for Data Processing",
                  content:
                    "Data processing is carried out in accordance with the GDPR. Legal bases include: consent (Art. 6(1)(a)), contract performance (Art. 6(1)(b)), legal obligations (Art. 6(1)(c)), and legitimate interests (Art. 6(1)(f)).",
                },
                {
                  heading: "Deletion and Retention Period",
                  content:
                    "Personal data is deleted or restricted as soon as the purpose for storage no longer applies. Storage may continue in cases of legal disputes or statutory retention obligations (§ 257 HGB, § 147 AO).",
                },
                {
                  heading: "Data Security",
                  content:
                    "We employ appropriate technical and organisational security measures to protect your data against manipulation, loss or unauthorised access, including TLS encryption for our website.",
                },
                {
                  heading: "Your Rights",
                  content: [
                    "• Right to access your data (Art. 15)",
                    "• Right to rectification of inaccurate data (Art. 16)",
                    "• Right to erasure (Art. 17)",
                    "• Right to restriction of processing (Art. 18)",
                    "• Right to data portability (Art. 20)",
                    "• Right to object (Art. 21)",
                    "• Right to lodge a complaint with a supervisory authority",
                    "Competent authority: LDI NRW, Kavalleriestr. 2-4, 40213 Düsseldorf",
                  ],
                },
              ],
            },
            {
              title: "B. Website Visits",
              sections: [
                {
                  heading: "Data Processed",
                  content: [
                    "Log data (server log files): abbreviated IP address, browser, operating system, page requested, date and time.",
                    "Contact form data: name, email, phone number, company, message.",
                  ],
                },
                {
                  heading: "Purpose and Legal Basis",
                  content:
                    "Log data is processed for statistical purposes and website security (Art. 6(1)(f)). Contact form data is processed to handle enquiries (Art. 6(1)(b) or Art. 6(1)(f)).",
                },
                {
                  heading: "Cookies",
                  content:
                    "We use only technically necessary cookies to ensure the proper functioning of the website. We do not use cookies for marketing or tracking purposes.",
                },
              ],
            },
          ]
        : [
            {
              title: "Vorwort",
              sections: [
                {
                  heading: "Vorwort",
                  content:
                    "Wir, TOP Buchhaltungsservice UG, Stuttgartstraße 15-17, 44143 Dortmund (haftungsbeschränkt), nehmen den Schutz Ihrer personenbezogenen Daten ernst und möchten Sie an dieser Stelle über den Datenschutz informieren. Mit dieser Erklärung informieren wir Sie darüber, in welcher Weise Ihre personenbezogenen Daten von uns verarbeitet werden.",
                },
              ],
            },
            {
              title: "A. Allgemeines",
              sections: [
                {
                  heading: "(1) Begriffsbestimmungen",
                  content: [
                    '– "Personenbezogene Daten" (Art. 4 Nr. 1 DS-GVO): alle Informationen, die sich auf eine identifizierte oder identifizierbare natürliche Person beziehen.',
                    '– "Verarbeiten" (Art. 4 Nr. 2 DS-GVO): jeder Vorgang, bei dem mit personenbezogenen Daten umgegangen wird.',
                    '– "Verantwortlicher" (Art. 4 Nr. 7 DS-GVO): die natürliche oder juristische Person, die über Zwecke und Mittel der Verarbeitung entscheidet.',
                    '– "Auftragsverarbeiter" (Art. 4 Nr. 8 DS-GVO): eine Stelle, die personenbezogene Daten im Auftrag des Verantwortlichen verarbeitet.',
                    '– "Einwilligung" (Art. 4 Nr. 11 DS-GVO): freiwillige, informierte und unmissverständliche Willensbekundung der betroffenen Person.',
                  ],
                },
                {
                  heading: "(2) Name und Anschrift des Verantwortlichen",
                  content: [
                    "TOP Buchhaltungsservice UG",
                    "Stuttgartstraße 15-17",
                    "44143 Dortmund",
                    "Tel.: +49 15755659651",
                    "E-Mail: info@top-bhs.de",
                    "Weitere Angaben: https://www.top-bhs.de/impressum",
                  ],
                },
                {
                  heading: "(3) Kontaktdaten des Datenschutzbeauftragten",
                  content: [
                    "Datenschutzbeauftragter",
                    "TOP Buchhaltungsservice UG",
                    "Stuttgartstraße 15-17",
                    "44143 Dortmund",
                    "info@top-bhs.de",
                  ],
                },
                {
                  heading: "(4) Rechtsgrundlagen der Datenverarbeitung",
                  content: [
                    '– Art. 6 Abs. 1 S. 1 lit. a DS-GVO ("Einwilligung"): freiwillige, informierte Zustimmung zur Datenverarbeitung.',
                    '– Art. 6 Abs. 1 S. 1 lit. b DS-GVO: Verarbeitung zur Vertragserfüllung oder vorvertraglicher Maßnahmen.',
                    '– Art. 6 Abs. 1 S. 1 lit. c DS-GVO: Verarbeitung zur Erfüllung einer rechtlichen Verpflichtung.',
                    '– Art. 6 Abs. 1 S. 1 lit. d DS-GVO: Verarbeitung zum Schutz lebenswichtiger Interessen.',
                    '– Art. 6 Abs. 1 S. 1 lit. e DS-GVO: Verarbeitung im öffentlichen Interesse.',
                    '– Art. 6 Abs. 1 S. 1 lit. f DS-GVO ("Berechtigte Interessen"): Verarbeitung zur Wahrung berechtigter Interessen.',
                  ],
                },
                {
                  heading: "(5) Datenlöschung und Speicherdauer",
                  content:
                    "Ihre personenbezogenen Daten werden gelöscht oder gesperrt, sobald der Zweck oder die Rechtsgrundlage für die Speicherung entfällt. Eine Speicherung erfolgt grundsätzlich nur auf unseren Servern in Deutschland. Eine längere Speicherung kann bei (drohenden) Rechtsstreitigkeiten oder aufgrund gesetzlicher Vorschriften (z. B. § 257 HGB, § 147 AO) erfolgen.",
                },
                {
                  heading: "(6) Datensicherheit",
                  content:
                    "Wir bedienen uns geeigneter technischer und organisatorischer Sicherheitsmaßnahmen (z. B. TLS-Verschlüsselung), um Ihre Daten gegen Manipulationen, Verlust, Zerstörung oder unbefugten Zugriff Dritter zu schützen. Unsere Sicherheitsmaßnahmen werden fortlaufend verbessert.",
                },
                {
                  heading: "(7) Zusammenarbeit mit Auftragsverarbeitern",
                  content:
                    "Wir setzen externe Dienstleister ein (z. B. für IT, Telekommunikation). Diese werden nur nach unserer Weisung tätig und wurden gemäß Art. 28 DS-GVO vertraglich zur Einhaltung der Datenschutzbestimmungen verpflichtet.",
                },
                {
                  heading: "(8) Weitergabe in Drittländer",
                  content:
                    "Im Rahmen unserer Geschäftsbeziehungen können Daten an Stellen außerhalb des EWR weitergegeben werden. Dies erfolgt ausschließlich zur Erfüllung vertraglicher Verpflichtungen. Wir stellen sicher, dass ein angemessenes Datenschutzniveau gewährleistet ist (Art. 44 ff. DS-GVO).",
                },
                {
                  heading: "(9) Keine automatisierte Entscheidungsfindung",
                  content:
                    "Wir haben nicht die Absicht, von Ihnen erhobene personenbezogene Daten für ein Verfahren zur automatisierten Entscheidungsfindung (einschließlich Profiling) zu verwenden.",
                },
                {
                  heading: "(10) Keine Verpflichtung zur Datenweitergabe",
                  content:
                    "Für Sie besteht grundsätzlich keine gesetzliche oder vertragliche Verpflichtung, uns Ihre personenbezogenen Daten zur Verfügung zu stellen. Bestimmte Angebote können wir jedoch eingeschränkt oder gar nicht erbringen, wenn Sie die erforderlichen Daten nicht bereitstellen.",
                },
                {
                  heading: "(11) Gesetzliche Übermittlungspflichten",
                  content:
                    "Wir können unter Umständen gesetzlich verpflichtet sein, rechtmäßig verarbeitete personenbezogene Daten für Dritte, insbesondere öffentliche Stellen, bereitzustellen (Art. 6 Abs. 1 S. 1 lit. c DS-GVO).",
                },
                {
                  heading: "(12) Ihre Rechte",
                  content: [
                    "– Recht auf Auskunft (Art. 15 DS-GVO)",
                    "– Recht auf Berichtigung unrichtiger Daten (Art. 16 DS-GVO)",
                    "– Recht auf Löschung (Art. 17 DS-GVO)",
                    "– Recht auf Einschränkung der Verarbeitung (Art. 18 DS-GVO)",
                    "– Recht auf Datenübertragbarkeit (Art. 20 DS-GVO)",
                    "– Recht auf Widerspruch (Art. 21 DS-GVO)",
                    "– Recht auf Widerruf einer erteilten Einwilligung (Art. 7 Abs. 3 DS-GVO)",
                    "– Beschwerderecht bei der Datenschutz-Aufsichtsbehörde (Art. 77 DS-GVO):",
                    "  Landesbeauftragte für Datenschutz und Informationsfreiheit NRW, Kavalleriestr. 2-4, 40213 Düsseldorf, Tel.: 0211/38424-0, E-Mail: poststelle@ldi.nrw.de",
                  ],
                },
                {
                  heading: "(13) Änderungen der Datenschutzhinweise",
                  content:
                    "Unsere Datenschutzhinweise werden regelmäßig auf Anpassungsbedarf geprüft. Über Änderungen werden Sie auf unserer Webseite www.top-bhs.de unterrichtet.",
                },
              ],
            },
            {
              title: "B. Besuch von Webseiten",
              sections: [
                {
                  heading: "(1) Erläuterung der Funktion",
                  content:
                    "Informationen zu unserem Unternehmen und unseren Leistungen erhalten Sie unter www.top-bhs.de. Bei einem Besuch unserer Webseiten können personenbezogene Daten verarbeitet werden.",
                },
                {
                  heading: "(2) Verarbeitete personenbezogene Daten",
                  content: [
                    'Protokolldaten (Server-Logfiles): Referrer-URL, Name und URL der angeforderten Seite, Datum und Uhrzeit, Browsertyp und -version, verkürzte IP-Adresse, übertragene Datenmenge, Betriebssystem, HTTP-Statuscode.',
                    'Kontaktformulardaten: Name, Anschrift, Firma, E-Mail-Adresse und Zeitpunkt der Übermittlung.',
                  ],
                },
                {
                  heading: "(3) Zweck und Rechtsgrundlage",
                  content:
                    "Die Verarbeitung der Protokolldaten dient statistischen Zwecken und der Verbesserung der Sicherheit und Stabilität der Verbindung (Rechtsgrundlage: Art. 6 Abs. 1 S. 1 lit. a oder lit. f DS-GVO). Die Verarbeitung von Kontaktformulardaten erfolgt zur Bearbeitung von Kundenanfragen (Rechtsgrundlage: Art. 6 Abs. 1 S. 1 lit. b oder lit. f DS-GVO).",
                },
                {
                  heading: "(4) Dauer der Datenverarbeitung",
                  content:
                    "Ihre Daten werden nur so lange verarbeitet, wie dies für die Erreichung der genannten Verarbeitungszwecke erforderlich ist.",
                },
                {
                  heading: "(5) Übermittlung an Dritte",
                  content: [
                    "– Dienstleister für den Betrieb unserer Webseite (z. B. Rechenzentrumsleistungen, IT-Sicherheit) – Rechtsgrundlage: Art. 6 Abs. 1 S. 1 lit. b oder lit. f DS-GVO.",
                    "– Staatliche Stellen/Behörden bei gesetzlicher Verpflichtung – Rechtsgrundlage: Art. 6 Abs. 1 S. 1 lit. c DS-GVO.",
                  ],
                },
                {
                  heading: "(6) Cookies",
                  content: [
                    "Wir nutzen auf unserer Webseite technisch notwendige Cookies, die für den Betrieb der Webseite erforderlich sind.",
                    "– Technical Cookies: zwingend erforderlich für grundlegende Funktionen und Sicherheit der Webseite.",
                    "– Performance Cookies: sammeln anonymisierte Informationen zur Nutzung der Webseite zur Verbesserung des Angebots.",
                    "Rechtsgrundlage: § 25 Abs. 2 Nr. 2 TTDSG bzw. Art. 6 Abs. 1 S. 1 lit. f DS-GVO.",
                  ],
                },
              ],
            },
          ];

  const title =
    currentLanguage === "ar"
      ? "سياسة الخصوصية"
      : currentLanguage === "en"
        ? "Privacy Policy"
        : "Datenschutzerklärung";

  const overline =
    currentLanguage === "ar"
      ? "قانوني"
      : currentLanguage === "en"
        ? "Legal"
        : "Rechtliches";

  return (
    <Container maxWidth="md" sx={{ py: 10, dir: isAr ? "rtl" : "ltr" }}>
      <Typography
        variant="overline"
        sx={{ color: "text.secondary", letterSpacing: 3, display: "block", mb: 2 }}
      >
        {overline}
      </Typography>
      <Typography variant="h2" sx={{ mb: 8 }}>
        {title}
      </Typography>

      {chapters.map((chapter, ci) => (
        <Box key={ci} sx={{ mb: 8 }}>
          {chapter.title !== "Vorwort" && chapter.title !== "Introduction" && chapter.title !== "مقدمة" && (
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, mb: 4, pb: 1.5, borderBottom: "2px solid #1a2640", color: "#1a2640" }}
            >
              {chapter.title}
            </Typography>
          )}

          {chapter.sections.map((section, idx) => (
            <Box key={idx} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                {section.heading}
              </Typography>
              {Array.isArray(section.content) ? (
                section.content.map((line, i) => (
                  <Typography key={i} variant="body1" sx={{ mb: 0.75, color: "text.secondary", lineHeight: 1.8 }}>
                    {line}
                  </Typography>
                ))
              ) : (
                <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8 }}>
                  {section.content}
                </Typography>
              )}
              {idx < chapter.sections.length - 1 && <Divider sx={{ mt: 3 }} />}
            </Box>
          ))}
        </Box>
      ))}
    </Container>
  );
};

export default DatenschutzPage;
